import React, { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabase';

const MarginsModal = ({ isOpen, onClose }) => {
  const [margins, setMargins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchMargins();
    }
  }, [isOpen]);

  const fetchMargins = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('brand_margins')
        .select('*')
        .order('brand');

      if (error) throw error;

      setMargins(data.map(margin => ({
        ...margin,
        increment: margin.margin_percentage,
        fixedAmount: margin.fixed_amount
      })));
    } catch (err) {
      console.error('Error al cargar márgenes:', err);
      setError('Error al cargar los márgenes. Por favor, intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const updates = margins.map(margin => ({
        id: margin.id,
        brand: margin.brand,
        margin_percentage: margin.increment,
        fixed_amount: margin.fixedAmount
      }));

      // 1. Guardar los márgenes
      const { error } = await supabase
        .from('brand_margins')
        .upsert(updates);

      if (error) throw error;

      // 2. Obtener el valor actual del dólar
      const { data: dolarData, error: dolarError } = await supabase
        .from('dolar')
        .select('valor')
        .order('id', { ascending: false })
        .limit(1);

      if (dolarError) throw dolarError;

      const dolarValor = dolarData?.[0]?.valor || 1;
      console.log('💵 Valor del dólar:', dolarValor);

      // 3. Crear un mapa de márgenes por marca/categoría (case-insensitive)
      const marginsMap = {};
      updates.forEach(m => {
        const key = (m.brand || '').toLowerCase().trim();
        marginsMap[key] = {
          margin_percentage: m.margin_percentage || 0,
          fixed_amount: m.fixed_amount || 0
        };
      });
      console.log('📋 Marcas en brand_margins:', Object.keys(marginsMap));

      // 4. Obtener todos los productos
      const { data: products, error: productsError } = await supabase
        .from('products')
        .select('*');

      if (productsError) throw productsError;

      // Obtener categorías únicas de productos para diagnóstico
      const uniqueCategories = [...new Set(products.map(p => p.categoria).filter(Boolean))];
      console.log('📦 Categorías en products:', uniqueCategories);

      // 5. Recalcular precios para cada producto
      const productsToUpdate = [];
      let matchedCount = 0;
      let skippedNoMargin = 0;
      let skippedNoPrice = 0;

      for (const product of products) {
        const categoria = product.categoria;
        const priceusd = product.priceusd || 0;

        // Buscar el margen correspondiente a la categoría (case-insensitive)
        const margin = categoria ? marginsMap[categoria.toLowerCase().trim()] : null;

        if (!margin) {
          skippedNoMargin++;
          continue;
        }

        if (priceusd <= 0) {
          skippedNoPrice++;
          continue;
        }

        matchedCount++;

        // Fórmula correcta:
        // price (USD) = priceusd × (1 + margin_percentage/100) + fixed_amount
        // pricears (ARS) = price × dolarValor
        const newPriceUSD = Math.round(
          (priceusd * (1 + margin.margin_percentage / 100)) +
          margin.fixed_amount
        );
        const newPriceARS = Math.round(newPriceUSD * dolarValor);

        // Solo actualizar si el precio cambió
        if (newPriceUSD !== product.price || newPriceARS !== product.pricears) {
          productsToUpdate.push({
            id: product.id,
            price: newPriceUSD,
            pricears: newPriceARS,
            margin_applied: margin.margin_percentage,
            last_price_update: new Date().toISOString()
          });
        }
      }

      console.log(`🔍 Productos analizados: ${products.length}`);
      console.log(`✅ Coincidencias con margen: ${matchedCount}`);
      console.log(`⏭️ Sin margen: ${skippedNoMargin}, sin precio USD: ${skippedNoPrice}`);
      console.log(`📝 Productos a actualizar: ${productsToUpdate.length}`);

      // 6. Actualizar los productos uno por uno para mejor manejo de errores
      if (productsToUpdate.length > 0) {
        console.log(`📊 Recalculando precios de ${productsToUpdate.length} productos...`);

        let successCount = 0;
        let failCount = 0;

        for (const productUpdate of productsToUpdate) {
          const { id, ...updates } = productUpdate;

          if (!id) {
            console.error('⚠️ Producto sin id, saltando:', productUpdate);
            failCount++;
            continue;
          }

          const { error: updateError } = await supabase
            .from('products')
            .update(updates)
            .eq('id', id);

          if (updateError) {
            console.error(`❌ Error actualizando producto ${id} (${updates.price} ARS):`, updateError);
            failCount++;
            // No lanzamos el error para que continue con los demás
          } else {
            successCount++;
          }
        }

        console.log(`✅ Productos actualizados: ${successCount} / ${productsToUpdate.length}`);
        if (failCount > 0) {
          console.warn(`⚠️ Productos que fallaron: ${failCount}`);
        }
      }

      onClose();
    } catch (err) {
      console.error('❌ Error al guardar márgenes:', err);
      setError(`Error: ${err.message || JSON.stringify(err)}`);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4">
        <div className="flex items-center gap-2 mb-4">
          <svg className="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          <h2 className="text-xl font-semibold">Márgenes por Marca</h2>
          <button
            onClick={onClose}
            className="ml-auto text-gray-400 hover:text-gray-600 transition-colors"
            disabled={saving}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <p className="text-gray-600 mb-6">
          Configura los incrementos de precio por marca. Los cambios se aplicarán automáticamente a todos los productos.
        </p>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
            <span className="ml-3 text-gray-600">Cargando márgenes...</span>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-4 mb-4 font-medium text-gray-600">
              <div>MARCA</div>
              <div>% INCREMENTO</div>
              <div>MONTO FIJO (USD)</div>
            </div>

            {margins.map((margin, index) => (
              <div key={margin.id} className="grid grid-cols-3 gap-4 mb-4 items-center">
                <div className="font-medium">{margin.brand}</div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    value={margin.increment}
                    onChange={(e) => {
                      const newMargins = [...margins];
                      newMargins[index].increment = Number(e.target.value);
                      setMargins(newMargins);
                    }}
                    className="w-20 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-right"
                    disabled={saving}
                  />
                  <span className="text-gray-500">%</span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    value={margin.fixedAmount}
                    onChange={(e) => {
                      const newMargins = [...margins];
                      newMargins[index].fixedAmount = Number(e.target.value);
                      setMargins(newMargins);
                    }}
                    className="w-20 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-right"
                    disabled={saving}
                  />
                  <span className="text-gray-500">USD</span>
                </div>
              </div>
            ))}

            <div className="flex justify-end gap-3 mt-8">
              <button
                onClick={onClose}
                className="px-6 py-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors font-medium"
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Guardando...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Guardar Cambios
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MarginsModal; 